import React, { useState, useEffect } from 'react';
import { ProcessArtefakt } from '../types/process';
import MermaidDiagram from './MermaidDiagram';
import ProcessDescription from './ProcessDescription';
import './ProcessViewer.css';

interface ProcessViewerProps {
  artefakt: ProcessArtefakt;
  onBack: () => void;
  onSave?: (updated: ProcessArtefakt) => void;
}

type Tab = 'diagram' | 'description' | 'editor';

const ProcessViewer: React.FC<ProcessViewerProps> = ({ artefakt, onBack, onSave }) => {
  const [activeTab, setActiveTab] = useState<Tab>('diagram');
  const [isEditing, setIsEditing] = useState(false);
  const [editedArtefakt, setEditedArtefakt] = useState<ProcessArtefakt>(artefakt);
  const [mermaidCode, setMermaidCode] = useState(artefakt.mermaid);

  // Update mermaidCode when artefakt changes
  useEffect(() => {
    setMermaidCode(artefakt.mermaid);
    setEditedArtefakt(artefakt);
  }, [artefakt]);

  const handleSave = () => {
    if (onSave) {
      onSave(editedArtefakt);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedArtefakt(artefakt);
    setIsEditing(false);
  };

  const handleSaveMermaidCode = () => {
    if (onSave) {
      const updated = { ...artefakt, mermaid: mermaidCode };
      onSave(updated);
      setEditedArtefakt(updated);
    }
  };

  const handleDownloadSVG = () => {
    const svgElement = document.querySelector('.mermaid-diagram svg');
    if (!svgElement) {
      alert('Kein Diagramm gefunden');
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentArtefakt.titel.replace(/[^a-z0-9]/gi, '_')}_diagram.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPNG = () => {
    const svgElement = document.querySelector('.mermaid-diagram svg') as SVGSVGElement;
    if (!svgElement) {
      alert('Kein Diagramm gefunden');
      return;
    }

    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get SVG dimensions
    const viewBox = svgElement.viewBox.baseVal;
    const width = viewBox.width || svgElement.width.baseVal.value || 800;
    const height = viewBox.height || svgElement.height.baseVal.value || 600;
    const scale = 2; // Higher resolution
    canvas.width = width * scale;
    canvas.height = height * scale;

    // Create an image from SVG
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          const pngUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = pngUrl;
          link.download = `${currentArtefakt.titel.replace(/[^a-z0-9]/gi, '_')}_diagram.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(pngUrl);
        }
      }, 'image/png');

      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  const currentArtefakt = isEditing ? editedArtefakt : artefakt;

  return (
    <div className="process-viewer">
      <div className="process-viewer-header">
        <button className="back-button" onClick={onBack}>
          ← Zurück
        </button>
        {isEditing ? (
          <input
            type="text"
            className="edit-title"
            value={editedArtefakt.titel}
            onChange={(e) => setEditedArtefakt({ ...editedArtefakt, titel: e.target.value })}
            style={{
              fontSize: '1.5rem',
              fontWeight: '300',
              padding: '0.5rem',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              flex: 1,
              marginRight: '1rem'
            }}
          />
        ) : (
          <h2>{currentArtefakt.titel}</h2>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {isEditing ? (
            <>
              <button className="btn-save" onClick={handleSave}>
                💾 Speichern
              </button>
              <button className="btn-cancel" onClick={handleCancel}>
                ✕ Abbrechen
              </button>
            </>
          ) : (
            <button className="btn-edit" onClick={() => setIsEditing(true)}>
              ✏️ Bearbeiten
            </button>
          )}
        </div>
        {!isEditing && (
          <div className="timestamp">
            Erstellt: {new Date(artefakt.erstelltAm).toLocaleString('de-DE')}
          </div>
        )}
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'diagram' ? 'active' : ''}`}
          onClick={() => setActiveTab('diagram')}
        >
          Diagramm
        </button>
        <button
          className={`tab ${activeTab === 'description' ? 'active' : ''}`}
          onClick={() => setActiveTab('description')}
        >
          Beschreibung
        </button>
        <button
          className={`tab ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          📝 Mermaid Editor
        </button>
        {activeTab === 'diagram' && !isEditing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleDownloadSVG}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              📥 SVG
            </button>
            <button
              onClick={handleDownloadPNG}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.9rem',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              📥 PNG
            </button>
          </div>
        )}
      </div>

      <div className="tab-content">
        {activeTab === 'diagram' ? (
          isEditing ? (
            <div style={{ padding: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Mermaid Code:
              </label>
              <textarea
                value={editedArtefakt.mermaid}
                onChange={(e) => setEditedArtefakt({ ...editedArtefakt, mermaid: e.target.value })}
                style={{
                  width: '100%',
                  minHeight: '300px',
                  padding: '1rem',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  resize: 'vertical'
                }}
              />
              <div style={{ marginTop: '1rem' }}>
                <strong>Vorschau:</strong>
                <MermaidDiagram code={editedArtefakt.mermaid} />
              </div>
            </div>
          ) : (
            <MermaidDiagram code={currentArtefakt.mermaid} />
          )
        ) : activeTab === 'editor' ? (
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Mermaid Code bearbeiten</h3>
              <button
                onClick={handleSaveMermaidCode}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                💾 Speichern
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', flex: 1, minHeight: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Mermaid Code eingeben:
                </label>
                <textarea
                  value={mermaidCode}
                  onChange={(e) => setMermaidCode(e.target.value)}
                  placeholder="flowchart TD&#10;    Start[Start] --> Ende[Ende]"
                  style={{
                    flex: 1,
                    padding: '1rem',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: '1.5',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    resize: 'none'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Live-Vorschau:
                </label>
                <div style={{
                  flex: 1,
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: 'white',
                  overflow: 'auto'
                }}>
                  <MermaidDiagram code={mermaidCode} />
                </div>
              </div>
            </div>
          </div>
        ) : isEditing ? (
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Problem:
              </label>
              <textarea
                value={editedArtefakt.beschreibung.problem}
                onChange={(e) => setEditedArtefakt({
                  ...editedArtefakt,
                  beschreibung: { ...editedArtefakt.beschreibung, problem: e.target.value }
                })}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Scope:
              </label>
              <textarea
                value={editedArtefakt.beschreibung.scope}
                onChange={(e) => setEditedArtefakt({
                  ...editedArtefakt,
                  beschreibung: { ...editedArtefakt.beschreibung, scope: e.target.value }
                })}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Hinweise (ein Hinweis pro Zeile):
              </label>
              <textarea
                value={editedArtefakt.hinweise.join('\n')}
                onChange={(e) => setEditedArtefakt({
                  ...editedArtefakt,
                  hinweise: e.target.value.split('\n').filter(h => h.trim())
                })}
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Erweiterte Bearbeitung (JSON):
              </label>
              <details>
                <summary style={{ cursor: 'pointer', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  Beschreibung als JSON bearbeiten (für Rollen, Prozesse, etc.)
                </summary>
                <textarea
                  value={JSON.stringify(editedArtefakt.beschreibung, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setEditedArtefakt({ ...editedArtefakt, beschreibung: parsed });
                    } catch (err) {
                      // Ignore invalid JSON while typing
                    }
                  }}
                  style={{
                    width: '100%',
                    minHeight: '400px',
                    padding: '0.75rem',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    resize: 'vertical',
                    marginTop: '0.5rem'
                  }}
                />
              </details>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <strong>Vorschau:</strong>
              <div style={{ marginTop: '0.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <ProcessDescription
                  beschreibung={editedArtefakt.beschreibung}
                  hinweise={editedArtefakt.hinweise}
                />
              </div>
            </div>
          </div>
        ) : (
          <ProcessDescription
            beschreibung={currentArtefakt.beschreibung}
            hinweise={currentArtefakt.hinweise}
          />
        )}
      </div>
    </div>
  );
};

export default ProcessViewer;
